import React, { useState } from "react";
import styles from "./AddMetricModal.module.scss";
import Icon from "../../../../common/Icon/Icon";
import Dropdown from "../../../../common/Dropdown/Dropdown";
import Button from "../../../../common/Button/Button";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../redux/hooks";
import isEmpty from "lodash/isEmpty";
import { useCreateGoalMetricMutation, useUpdateGoalMetricMutation } from "../../../../../redux/services/goalMetricAPI";
import { useProfileParams } from "../../../../../utils/common";
import { ACTIONS } from "../../../../../constants/dataConstants";

type PerformanceArea = {
  code: string;
  value: string;
};

type performanceMetric = {
  [category: string]: PerformanceArea[];
};

interface MetricsSectionProps {
  handleOperation: (item: string, gridId: string | number | null) => void;
  performanceAreas: PerformanceArea[];
  performanceMetric: performanceMetric;
  onChange: (metrics: MetricRow[]) => void;
  onClose: () => void;
  selectedMetricsMap: any;
  selectedActionId: string | number
}

const AddMetricModal: React.FC<MetricsSectionProps> = ({
  handleOperation,
  performanceAreas,
  performanceMetric,
  onChange,
  onClose,
  selectedMetricsMap,
  selectedActionId,
}) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const createDefaultMetric = (
    areaId?: string,
    selectedMetric?: MetricRow,
    metricId?: string,
  ): MetricRow => {
    const area =
      performanceAreas.find((a) => a.code === areaId) || performanceAreas[0];
    const metric =
      performanceMetric[area?.value].find((m: any) => m.code === metricId) ||
      performanceMetric[area?.value][0];
    return {
      aboGoalActionId: selectedActionId || "",
      aboGoalActionMetricId: selectedMetric?.aboGoalActionMetricId || null,
      kpiTypeCode: area?.code || "",
      kpiCode: metric?.code || "",
      status: 1,
    };
  };

  const [metrics, setMetrics] = useState<MetricRow[]>(!isEmpty(selectedMetricsMap[selectedActionId]) ? selectedMetricsMap[selectedActionId] : [createDefaultMetric()]);
  const { affiliateCode, aboNumber } = useProfileParams();

  const [
    createGoalMetric,
    { isLoading: isLoadingCreateGoalMetric, isError: isCreateGoalMetricError },
  ] = useCreateGoalMetricMutation();

  const [
    updateGoalMetric,
    { isLoading: isLoadingUpdateGoalMetric, isError: isUpdateGoalMetricError },
  ] = useUpdateGoalMetricMutation();

  const updateMetrics = (newMetrics: MetricRow[]) => {
    setMetrics(newMetrics);
  };

  const handleChange = (index: number, changes: Partial<MetricRow>) => {
    const updated = metrics.map((m, i) =>
      i === index ? { ...m, ...changes } : m
    );
    updateMetrics(updated);
  };

  const handlePerformanceAreaChange = (index: number, areaId: string, selectedMetric: MetricRow) => {
    handleChange(index, createDefaultMetric(areaId, selectedMetric));
  };

  const handleMetricChange = (index: number, metricId: string, selectedMetric: MetricRow) => {
    const areaId = metrics[index].kpiTypeCode;
    handleChange(index, createDefaultMetric(areaId, selectedMetric, metricId ));
  };

  const addMetric = () => {
    if (metrics.length < 3) updateMetrics([...metrics, createDefaultMetric()]);
  };

  const removeMetric = (index: number) => {
    const metricId = metrics[index]?.aboGoalActionMetricId;
    handleOperation(ACTIONS.DELETE_METRICS, metricId);
    updateMetrics(metrics.filter((_, i) => i !== index));
  };

  const handleMetricSave = () => {
    const checkAllIdsNull = metrics?.every((item) => item?.aboGoalActionMetricId === null);
    const createUpdateMetricAPI = checkAllIdsNull ? createGoalMetric : updateGoalMetric;
    createUpdateMetricAPI({
      affiliateCode,
      aboNumber,
      payload: metrics,
    }).then((resp: any) => {
      const goalMetricRespData = resp?.data?.body;
      if (!isEmpty(goalMetricRespData)) {
        onChange(goalMetricRespData);
      }
    });
  };

  return (
    <div className={styles.add_metrics_modal_container}>
      <div className={styles.modal_wrapper}>
        <div className={styles.modal_title}>{t("add_edit_metric")}</div>
        <div className={styles.subtext}>
          {t("select_three_metric")}
        </div>

        {metrics?.map((m, i) => {
          const areaValue: string =
            performanceAreas.find((pa) => pa.code === m.kpiTypeCode)?.value ||
            "";
          const availableMetrics =
            performanceMetric[areaValue] || performanceMetric["Pipeline"];

          return (
            <div key={m?.aboGoalActionMetricId} className={styles.metric_row}>
              <div className={styles.metric_title}>{`${t("metric")} #${i + 1}`}</div>
              <div className={styles.inner_wrapper}>
                <div className={styles.dropdown_wrapper}>
                  <div className={styles.dropdown_holder}>
                    <Dropdown
                      dropdownName={t("performance_area")}
                      items={performanceAreas.map((pa) => ({
                        code: pa.code,
                        value: pa.value,
                      }))}
                      selectedItem={{
                        code: m.kpiTypeCode,
                        value:
                          performanceAreas.find(
                            (pa) => pa.code === m.kpiTypeCode
                          )?.value || "",
                      }}
                      handleSelectItemCallback={(item) =>
                        handlePerformanceAreaChange(i, String(item?.code), m)
                      }
                      addScroll
                      className={styles.form_dropdown}
                    />
                  </div>
                  <div className={styles.dropdown_holder}>
                    <Dropdown
                      dropdownName={t("metric")}
                      items={availableMetrics.map((metric: PerformanceArea) => ({
                        code: metric.code,
                        value: metric.value,
                      }))}
                      selectedItem={{
                        code: m.kpiCode,
                        value:
                          availableMetrics.find(
                            (metric: PerformanceArea) =>
                              metric.code === m.kpiCode
                          )?.value || "",
                      }}
                      handleSelectItemCallback={(item) =>
                        handleMetricChange(i, String(item.code), m)
                      }
                      addScroll
                      className={styles.form_dropdown}
                    />
                    {metrics.length > 1 && i > 0 && (
                      <button
                        type="button"
                        className={styles.delete_button}
                        onClick={() => removeMetric(i)}
                      >
                        <Icon
                          name="trash"
                          size={21}
                          color={
                            currentTheme === "light" ? "#2c2c2c" : "#D8D8D9"
                          }
                        />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {metrics?.length < 3 && (
          <div className={styles.add_metric_btn_wrapper}>
            <button
              type="button"
              className={styles.add_metric_btn}
              onClick={addMetric}
              disabled={metrics.length >= 3}
            >
              <Icon
                name="plus_white"
                size={14}
                className={styles.plus_icon}
                color={currentTheme === "dark" ? "#D8D8D9" : "#2C2C2C"}
              />
              <span>{t("add_another_metric")}</span>
            </button>
          </div>
        )}

        <div className={`btn_wrapper ${styles.btn_wrapper}`}>
          <Button
            id="cancelAction"
            onClick={onClose}
            label={t("AP_info.btnCancel")}
            style="unfilled"
          />
          <Button
            id="saveAction"
            onClick={() => {
              handleMetricSave();
              onClose();
            }}
            label={t("AP_info.btnSave")}
            style="filled"
          />
        </div>
      </div>
    </div>
  );
};

export default AddMetricModal;
